<template>
  <div class="landing-page">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-content">
        <div class="hero-text">
          <h1 class="hero-title">{{ $t("website.hero_title") }}</h1>
          <h2 class="hero-subtitle">{{ $t("website.hero_subtitle") }}</h2>
          <p class="hero-description">
            {{ $t("website.hero_description") }}
          </p>
          <div class="hero-cta">
            <NuxtLink to="/contact" class="btn btn-primary btn-large">{{
              $t("website.contact_us")
            }}</NuxtLink>
          </div>
          <div class="hero-badges">
            <NuxtLink to="/freelancer" class="hero-link">
              <i class="fa fa-user-tie"></i>
              <span>{{ $t("website.for_freelancers") }}</span>
            </NuxtLink>
            <span class="separator">|</span>
            <NuxtLink to="/companies" class="hero-link">
              <i class="fa fa-building"></i>
              <span>{{ $t("website.for_companies") }}</span>
            </NuxtLink>
          </div>
        </div>
        <div class="hero-image">
          <ComputerScreenMockup :enableHover="true" :rotateDirection="'left'">
            <img
              src="@/assets/website/startpage/dashboard.png"
              alt="Modern time tracking dashboard interface"
            />
          </ComputerScreenMockup>
        </div>
      </div>
    </section>

    <!-- Problem/Challenge Section -->
    <section class="problem-section">
      <div class="problem-content">
        <h2 class="section-title gradient-text">
          {{ $t("website.time_tracking_doesnt_have_to_be_complicated") }}
        </h2>
        <div class="title-underline"></div>
        <p class="section-subtitle">
          {{ $t("website.many_face_daily_challenges") }}
        </p>

        <div class="problems-grid">
          <div class="problem-card">
            <div class="problem-icon">
              <i class="fa fa-clock"></i>
            </div>
            <h3>{{ $t("website.lost_time") }}</h3>
            <p>
              {{ $t("website.lost_time_description") }}
            </p>
          </div>
          <div class="problem-card">
            <div class="problem-icon">
              <i class="fa fa-euro-sign"></i>
            </div>
            <h3>{{ $t("website.missed_revenue") }}</h3>
            <p>
              {{ $t("website.missed_revenue_description") }}
            </p>
          </div>
          <div class="problem-card">
            <div class="problem-icon">
              <i class="fa fa-tasks"></i>
            </div>
            <h3>{{ $t("website.chaotic_project_management") }}</h3>
            <p>
              {{ $t("website.chaotic_project_management_description") }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Solution Section -->
    <section class="solution-section">
      <div class="solution-content">
        <h2 class="section-title gradient-text">
          {{ $t("website.everything_in_one_place") }}
        </h2>
        <div class="title-underline"></div>
        <p class="section-description">
          {{ $t("website.everything_in_one_place_description") }}
        </p>

        <div class="solution-grid">
          <div class="solution-feature">
            <div class="solution-icon">
              <i class="fa fa-stopwatch"></i>
            </div>
            <h3>{{ $t("website.time_tracking") }}</h3>
            <p>{{ $t("website.time_tracking_feature_description") }}</p>
          </div>
          <div class="solution-feature">
            <div class="solution-icon">
              <i class="fa fa-project-diagram"></i>
            </div>
            <h3>{{ $t("website.project_management") }}</h3>
            <p>{{ $t("website.project_management_feature_description") }}</p>
          </div>
          <div class="solution-feature">
            <div class="solution-icon">
              <i class="fa fa-users"></i>
            </div>
            <h3>{{ $t("website.customer_management") }}</h3>
            <p>{{ $t("website.customer_management_feature_description") }}</p>
          </div>
          <div class="solution-feature">
            <div class="solution-icon">
              <i class="fa fa-file-invoice"></i>
            </div>
            <h3>{{ $t("website.billing") }}</h3>
            <p>
              {{ $t("website.billing_feature_description") }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Detailed Features Section -->
    <section id="demo" class="detailed-features">
      <div class="detailed-features-content">
        <h2 class="section-title gradient-text">
          {{ $t("website.detailed_features") }}
        </h2>
        <div class="title-underline"></div>
        <p class="section-description">
          {{ $t("website.discover_the_tools") }}
        </p>

        <div class="detailed-feature-cards">
          <div class="detailed-feature">
            <div class="feature-number">01</div>
            <div class="feature-content">
              <h3>{{ $t("website.customer_management_title") }}</h3>
              <p>
                {{ $t("website.customer_management_detailed_description") }}
              </p>
              <a href="#" class="learn-more"
                >{{ $t("website.learn_more") }} <i class="fa fa-arrow-right"></i
              ></a>
            </div>
            <div class="feature-image">
              <ComputerScreenMockup
                :enableHover="true"
                :rotateDirection="'left'"
                maxWidth="700px"
                maxHeight="500px"
                :showStand="false"
                :showCamera="false"
              >
                <img
                  src="@/assets/website/startpage/customers.png"
                  alt="Kundenseite mit Kundendaten, Aktivitätenübersicht und Zeiteinträgen"
                />
              </ComputerScreenMockup>
            </div>
          </div>

          <div class="detailed-feature reverse">
            <div class="feature-number">02</div>
            <div class="feature-content">
              <h3>{{ $t("website.project_management_title") }}</h3>
              <p>
                {{ $t("website.project_management_detailed_description") }}
              </p>
              <a href="#" class="learn-more"
                >{{ $t("website.learn_more") }} <i class="fa fa-arrow-right"></i
              ></a>
            </div>
            <div class="feature-image">
              <ComputerScreenMockup
                :enableHover="true"
                :rotateDirection="'right'"
                maxWidth="700px"
                maxHeight="500px"
                :showStand="false"
                :showCamera="false"
              >
                <img
                  src="@/assets/website/startpage/projects.png"
                  alt="Projektseite mit Projektdaten, Budgetcharts und Zeiteinträgen"
                />
              </ComputerScreenMockup>
            </div>
          </div>

          <div class="detailed-feature">
            <div class="feature-number">03</div>
            <div class="feature-content">
              <h3>{{ $t("website.digital_clock_title") }}</h3>
              <p>
                {{ $t("website.digital_clock_description") }}
              </p>
              <a href="#" class="learn-more"
                >{{ $t("website.learn_more") }} <i class="fa fa-arrow-right"></i
              ></a>
            </div>
            <div class="feature-image">
              <ComputerScreenMockup
                :enableHover="true"
                :rotateDirection="'left'"
                maxWidth="700px"
                maxHeight="500px"
                :showStand="false"
                :showCamera="false"
              >
                <img
                  src="@/assets/website/startpage/digital-clock.png"
                  alt="Digitale Uhr mit laufender Stoppuhrfunktion und ausgewähltem Kunden, Projekt und Aktivität"
                />
              </ComputerScreenMockup>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Key Features Section -->
    <section class="key-features">
      <h2 class="section-title gradient-text">
        {{ $t("website.key_features") }}
      </h2>
      <div class="title-underline"></div>
      <p class="section-subtitle">
        {{ $t("website.key_features_subtitle") }}
      </p>
      <PagesFeaturesOverview :highlightFirst="true" />
    </section>

    <!-- Target Audience Section -->
    <section class="target-audience">
      <div class="target-audience-header">
        <h2 class="section-title gradient-text">
          {{ $t("website.for_whom") }}
        </h2>
        <div class="title-underline"></div>
        <p class="section-subtitle">
          {{ $t("website.choose_your_path") }}
        </p>
      </div>

      <div class="audience-cards">
        <!-- Freelancer Card -->
        <div class="audience-card">
          <div class="popular-badge">{{ $t("website.most_popular") }}</div>
          <h3>{{ $t("website.freelancer_key") }}</h3>
          <p>
            {{ $t("website.freelancer_full_description") }}
          </p>
          <ul class="feature-list">
            <li>
              <i class="fa fa-check"></i>
              {{ $t("website.personal_time_tracking") }}
            </li>
            <li>
              <i class="fa fa-check"></i> {{ $t("website.client_management") }}
            </li>
            <li>
              <i class="fa fa-check"></i> {{ $t("website.invoice_generation") }}
            </li>
            <li>
              <i class="fa fa-check"></i> {{ $t("website.project_budgets") }}
            </li>
          </ul>
          <NuxtLink to="/freelancer" class="btn btn-primary">{{
            $t("website.for_freelancers")
          }}</NuxtLink>
        </div>

        <!-- Unternehmen Card -->
        <div class="audience-card">
          <h3>{{ $t("website.companies") }}</h3>
          <p>
            {{ $t("website.companies_full_description") }}
          </p>
          <ul class="feature-list">
            <li>
              <i class="fa fa-check"></i> {{ $t("website.team_collaboration") }}
            </li>
            <li>
              <i class="fa fa-check"></i> {{ $t("website.advanced_reporting") }}
            </li>
            <li>
              <i class="fa fa-check"></i>
              {{ $t("website.multi_project_tracking") }}
            </li>
            <li>
              <i class="fa fa-check"></i>
              {{ $t("website.enterprise_security") }}
            </li>
          </ul>
          <NuxtLink to="/companies" class="btn btn-primary">{{
            $t("website.for_companies")
          }}</NuxtLink>
        </div>
      </div>
    </section>

    <!-- Mobile Section -->
    <section class="mobile-section">
      <div class="mobile-content">
        <div class="mobile-image">
          <PhoneMockup :enableHover="true" :rotateDirection="'right'">
            <img
              src="@/assets/website/startpage/digital-clock-mobile.png"
              alt="Digital clock on smartphone with running stopwatch function"
              class="phone-image"
            />
          </PhoneMockup>
        </div>
        <div class="mobile-text">
          <h2>{{ $t("website.always_with_you_on_mobile") }}</h2>
          <p>
            {{ $t("website.always_with_you_on_mobile_description") }}
          </p>
          <ul class="mobile-features">
            <li>
              <i class="fa fa-mobile-alt"></i>
              {{ $t("website.responsive_web_app") }}
            </li>
            <li>
              <i class="fa fa-sync"></i> {{ $t("website.realtime_sync") }}
            </li>
            <li>
              <i class="fa fa-wifi"></i>
              {{ $t("website.available_everywhere") }}
            </li>
          </ul>
          <NuxtLink to="/contact" class="btn btn-primary">{{
            $t("website.contact_us")
          }}</NuxtLink>
        </div>
      </div>
    </section>

    <!-- Trust & Social Proof Section -->
    <section class="trust-section">
      <h2 class="section-title gradient-text">
        {{ $t("website.trust_chronofox") }}
      </h2>
      <div class="title-underline"></div>
      <p class="section-subtitle">
        {{ $t("website.professionals_use_chronofox") }}
      </p>

      <ProfessionsMarquee />

      <div class="trust-features">
        <div class="trust-item">
          <i class="fa fa-shield-alt"></i>
          <h4>{{ $t("website.gdpr_compliant") }}</h4>
          <p>{{ $t("website.your_data_is_safe") }}</p>
        </div>
        <div class="trust-item">
          <i class="fa fa-headset"></i>
          <h4>{{ $t("website.german_support") }}</h4>
          <p>{{ $t("website.help_in_german") }}</p>
        </div>
        <div class="trust-item">
          <i class="fa fa-rocket"></i>
          <h4>{{ $t("website.easy_start") }}</h4>
          <p>{{ $t("website.ready_in_minutes") }}</p>
        </div>
      </div>
    </section>

    <!-- Final CTA Section -->
    <section class="final-cta">
      <h2>{{ $t("website.start_with_chronofox_today") }}</h2>
      <p>
        {{ $t("website.get_in_touch_to_get_started") }}
      </p>
      <div class="cta-buttons">
        <NuxtLink to="/contact" class="btn btn-primary btn-large">
          {{ $t("website.contact_us") }}
        </NuxtLink>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: "website",
});

const { t, locale } = useI18n();

import logo from "@/assets/logo/chronofox_wortbild_right.png";
useSeoMeta({
  title: t("website.hero_title") + " | " + t("app_name"),
  description: t("website.hero_description"),

  ogTitle: t("app_name") + " - " + t("website.hero_title"),
  ogDescription: t("website.hero_description"),
  ogLocale: locale.value ? locale.value : "de",
  ogType: "website",
  ogImage: logo,

  twitterCard: "summary_large_image",
  twitterTitle: t("website.hero_title") + " | " + t("app_name"),
  twitterDescription: t("website.hero_description"),
  twitterImage: logo,
  twitterSite: "@ChronoFoxApp",
});
</script>

<style scoped>
.landing-page {
  width: 100%;
  overflow-x: hidden;
}

/* Hero Section */
.hero {
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  color: white;
  padding: 120px 20px 80px;
  position: relative;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 4fr 5fr;
  gap: 80px;
  align-items: center;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  line-height: 1.2;
}

.hero-subtitle {
  font-size: 2rem;
  font-weight: 300;
  margin-bottom: 1.5rem;
  opacity: 0.95;
}

.hero-description {
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  opacity: 0.9;
}

.hero-cta {
  display: flex;
  gap: 15px;
  margin-bottom: 2rem;
}

.hero-badges {
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 0.95rem;
  opacity: 0.95;
}

.hero-link {
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  padding: 8px 0;
}

.hero-link:hover {
  opacity: 0.8;
  transform: translateX(2px);
}

.hero-link i {
  font-size: 1.1rem;
}

.separator {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
}

.hero-image {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-image :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Buttons */
.btn {
  padding: 14px 32px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
  text-align: center;
}

.btn-primary {
  background: white;
  color: #ff6b35;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.btn-secondary {
  background: transparent;
  color: white;
  border: 2px solid white;
}

.btn-secondary:hover {
  background: white;
  color: #ff6b35;
}

.btn-secondary-outline {
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.8);
  color: white;
  backdrop-filter: blur(10px);
}

.btn-secondary-outline:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: white;
}

.btn-large {
  padding: 18px 42px;
  font-size: 1.1rem;
}

/* Stats Section */
.stats {
  background: white;
  padding: 60px 20px;
  margin-top: -40px;
  position: relative;
  z-index: 10;
}

.stats-container {
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 15px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 40px;
}

.stat-item {
  text-align: center;
  padding: 20px;
}

.stat-value {
  font-size: 3rem;
  font-weight: 800;
  color: #ff6b35;
  margin-bottom: 10px;
}

.stat-label {
  font-size: 1rem;
  color: #6b7280;
  font-weight: 500;
}

/* Problem Section */
.problem-section {
  padding: 120px 20px;
  background: #f9fafb;
  text-align: center;
}

.problem-content {
  max-width: 1200px;
  margin: 0 auto;
}

.problems-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-top: 60px;
}

.problem-card {
  background: white;
  padding: 40px;
  border-radius: 15px;
  border: 2px solid #e5e7eb;
  transition: all 0.3s ease;
}

.problem-card:hover {
  transform: translateY(-5px);
  border-color: #ff6b35;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.problem-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 1.8rem;
  color: #dc2626;
}

.problem-card h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 15px;
}

.problem-card p {
  color: #6b7280;
  line-height: 1.7;
}

/* Solution Section */
.solution-section {
  padding: 120px 20px;
  background: white;
  text-align: center;
}

.solution-content {
  max-width: 1200px;
  margin: 0 auto;
}

.solution-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-top: 60px;
}

.solution-feature {
  padding: 30px 20px;
  text-align: center;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  background: white;
  transition: all 0.3s ease;
  position: relative;
  cursor: pointer;
}

.solution-feature::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.solution-feature:hover::before {
  opacity: 0.05;
}

.solution-feature:hover {
  transform: translateY(-5px);
  border-color: #ff6b35;
  box-shadow: 0 8px 15px rgba(255, 107, 53, 0.15);
}

.solution-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 1.8rem;
  color: white;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.solution-feature:hover .solution-icon {
  transform: scale(1.05) rotate(3deg);
}

.solution-feature h3 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 10px;
}

.solution-feature p {
  color: #6b7280;
  line-height: 1.6;
  font-size: 0.95rem;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.section-subtitle {
  font-size: 1.2rem;
  color: #6b7280;
  margin-bottom: 4rem;
  margin-top: 1rem;
}

.section-description {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #4b5563;
  margin-bottom: 2rem;
}

.learn-more {
  color: #ff6b35;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: gap 0.3s ease;
}

.learn-more:hover {
  gap: 12px;
}

/* Key Features Section */
.key-features {
  padding: 120px 20px;
  background: #f9fafb;
  text-align: center;
}

/* Detailed Features Section */
.detailed-features {
  padding: 120px 20px;
  background: white;
}

.detailed-features-content {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.detailed-feature-cards {
  margin: 60px 0 0;
}

.detailed-feature {
  display: grid;
  grid-template-columns: 80px 1fr 1fr;
  gap: 40px;
  align-items: center;
  margin-bottom: 140px;
  text-align: left;
}

.detailed-feature.reverse {
  grid-template-columns: 80px 1fr 1fr;
}

.detailed-feature.reverse .feature-content {
  order: 2;
}

.detailed-feature.reverse .feature-image {
  order: 1;
}

.feature-number {
  font-size: 4rem;
  font-weight: 800;
  color: #e5e7eb;
}

.feature-content h3 {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 15px;
}

.feature-content p {
  color: #4b5563;
  line-height: 1.8;
  margin-bottom: 20px;
}

.feature-image :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Target Audience Section */
.target-audience {
  padding: 120px 20px;
  background: white;
  text-align: center;
}

.target-audience-header {
  margin-bottom: 60px;
}

.gradient-text {
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 3rem;
  margin-bottom: 0.5rem;
}

.title-underline {
  width: 80px;
  height: 4px;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  margin: 0 auto 1rem;
  border-radius: 2px;
}

.audience-cards {
  max-width: 1200px;
  margin: 40px auto 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
}

.audience-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 15px;
  padding: 40px;
  position: relative;
  display: flex;
  flex-direction: column;
}

.audience-card .btn-primary {
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  color: white;
  margin-top: auto;
}

.audience-card .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(255, 107, 53, 0.3);
}

.popular-badge {
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  color: white;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

.audience-card h3 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 20px;
}

.audience-card p {
  color: #4b5563;
  line-height: 1.8;
  margin-bottom: 25px;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin-bottom: 30px;
}

.feature-list li {
  padding: 12px 0;
  color: #4b5563;
  display: flex;
  align-items: center;
  gap: 12px;
}

.feature-list i {
  color: #10b981;
  font-size: 1.1rem;
}

/* Mobile Section */
.mobile-section {
  padding: 120px 20px;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  color: white;
}

.mobile-content {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.mobile-image {
  display: flex;
  justify-content: center;
  align-items: center;
}

.mobile-image :deep(.phone-image) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mobile-text h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
}

.mobile-text p {
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  opacity: 0.9;
}

.mobile-features {
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
}

.mobile-features li {
  padding: 12px 0;
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 1.1rem;
}

.mobile-features i {
  font-size: 1.3rem;
}

/* Pricing Section */
.pricing {
  padding: 120px 20px;
  background: #f9fafb;
  text-align: center;
}

.pricing-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* All Features Section */
.all-features {
  padding: 120px 20px;
  background: white;
  text-align: center;
}

/* Trust & Social Proof Section */
.trust-section {
  padding: 120px 20px;
  background: #f9fafb;
  text-align: center;
}

.trust-features {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  max-width: 1000px;
  margin: 60px auto 0;
}

.trust-item {
  padding: 30px;
}

.trust-item i {
  font-size: 3rem;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 15px;
}

.trust-item h4 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 10px;
}

.trust-item p {
  color: #6b7280;
  font-size: 1rem;
}

/* Final CTA Section */
.final-cta {
  padding: 100px 20px;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  color: white;
  text-align: center;
}

.final-cta h2 {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
}

.final-cta p {
  font-size: 1.3rem;
  margin-bottom: 2.5rem;
  opacity: 0.9;
}

.cta-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 2rem;
}

.cta-note {
  font-size: 1rem;
  opacity: 0.95;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 25px;
  flex-wrap: wrap;
}

.cta-note-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.cta-note-item i {
  flex-shrink: 0;
}

/* Responsive Design */
@media (max-width: 1050px) {
  .hero-image {
    display: none;
  }

  .hero-content {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1024px) {
  .hero-content,
  .mobile-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .problems-grid,
  .trust-features,
  .solution-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .pricing-cards {
    grid-template-columns: 1fr;
    max-width: 500px;
  }

  .detailed-feature,
  .detailed-feature.reverse {
    grid-template-columns: 1fr;
  }

  .feature-number {
    font-size: 3rem;
  }

  .stats-container {
    grid-template-columns: 1fr;
    gap: 30px;
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.5rem;
  }

  .audience-cards,
  .problems-grid,
  .solution-grid,
  .trust-features {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: 2rem;
  }

  .hero-cta,
  .convince-links,
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }

  .btn {
    width: 100%;
    max-width: 300px;
    text-align: center;
  }

  .mobile-text h2,
  .final-cta h2 {
    font-size: 2rem;
  }
}
</style>
